import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { CrmService, EmployeeProfile } from '../../../../../services/crm.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { ModalService } from '../../../../../services/modal.service';
import { FileUploadNewComponent } from '../../../../../modals/file-upload-new/file-upload-new.component';
import { IError, IGroups } from '../../../../../interfaces';
import { Tab } from '../../../../tabs/interfaces/tab.interface';
import { EmployeeProfileTab } from '../../../../tabs/enums/tabs.enum';
import {
  activeTranslations,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
} from '../../../../../enum/constants';
import { formatFullName } from '../../../../../helpers';
import { TIMELINE_FILTERS } from '../../../../timeline/timeline.component';

export interface EmployeeProfilePayload {
  employeeId: string;
  canEdit: boolean;
}

const MAX_AVATAR_SIZE_MB = 5;

/** What FileUploadNewComponent returns for files uploaded to the files service (`key` is e.g. `avatars/<uuid>.jpg`) */
interface UploadedTempFile {
  key: string;
  link: string;
  name: string;
  mimeType: string;
}

const requiredTrimmed = (control: AbstractControl): ValidationErrors | null =>
  String(control.value ?? '').trim() ? null : { required: true };

@Component({
  selector: 'app-employee-profile',
  templateUrl: './employee-profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class EmployeeProfileComponent implements OnInit {
  @Input() payload!: EmployeeProfilePayload;
  @Output() close = new EventEmitter<'success' | 'close'>();

  private readonly crmService = inject(CrmService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected employee: EmployeeProfile | null = null;
  protected groups: IGroups[] = [];
  protected isLoading = true;
  protected isEditing = false;
  protected isSaving = false;
  protected isUploadingAvatar = false;
  protected avatarPreviewUrl: string | null = null;
  private hasChanges = false;

  protected readonly tabs: Tab<EmployeeProfileTab>[] = [
    { name: 'general', value: EmployeeProfileTab.GENERAL, default: true },
    { name: 'board_v3_tab_timeline_history', value: EmployeeProfileTab.HISTORY },
    { name: 'analytics', value: EmployeeProfileTab.ANALYTICS, disabled: true, dev: true },
  ];
  protected activeTab = EmployeeProfileTab.GENERAL;
  protected readonly EmployeeProfileTab = EmployeeProfileTab;
  protected readonly TIMELINE_FILTERS = TIMELINE_FILTERS;

  protected readonly form = new FormGroup({
    first_name: new FormControl('', { nonNullable: true, validators: requiredTrimmed }),
    last_name: new FormControl('', { nonNullable: true, validators: requiredTrimmed }),
    group_id: new FormControl<string | null>(null, Validators.required),
  });

  ngOnInit(): void {
    this.loadEmployee();
    if (this.payload?.canEdit) this.loadGroups();
  }

  protected get fullName(): string {
    return formatFullName(this.employee?.first_name, this.employee?.last_name) || '-';
  }

  protected get avatarUrl(): string | undefined {
    return this.avatarPreviewUrl || this.employee?.avatar_url || undefined;
  }

  protected onSelectTab(tab: Tab<EmployeeProfileTab>): void {
    if (!tab.disabled) this.activeTab = tab.value;
  }

  protected onClose(): void {
    this.close.emit(this.hasChanges ? 'success' : 'close');
  }

  protected startEditing(): void {
    if (!this.payload?.canEdit || !this.employee) return;

    this.form.reset({
      first_name: this.employee.first_name || '',
      last_name: this.employee.last_name || '',
      group_id: this.employee.group_id || null,
    });
    this.isEditing = true;
  }

  protected cancelEditing(): void {
    this.isEditing = false;
  }

  protected isInvalid(field: keyof typeof this.form.controls): boolean {
    const control = this.form.controls[field];
    return control.touched && control.invalid;
  }

  protected save(): void {
    if (this.isSaving || !this.employee) return;

    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const { first_name, last_name, group_id } = this.form.getRawValue();
    this.isSaving = true;
    this.crmService
      .updateManager({ first_name: first_name.trim(), last_name: last_name.trim(), group_id }, this.employee.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (employee: EmployeeProfile) => {
          this.employee = { ...this.employee!, ...employee };
          this.hasChanges = true;
          this.isEditing = false;
          this.notificationService.showSuccess('', activeTranslations.notifications_texts.success.successfully_saved);
        },
        error: (e) => this.showErrors(e),
      });
  }

  protected openAvatarUpload(): void {
    if (!this.employee || this.isUploadingAvatar) return;

    this.modalService
      .openModal({
        component: FileUploadNewComponent,
        title: 'employee_profile_upload_photo',
        translate: true,
        height: 'auto',
        data: {
          max_upload_files: 1,
          // Valid for the file picker `accept` and for the modal's own `file.type.match` check
          allowed_media_types: ['image/*'],
          max_upload_size: MAX_AVATAR_SIZE_MB,
          is_tmp: true,
          upload_path: 'avatar',
          channel_type: null,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((files: UploadedTempFile[]) => {
        const file = files?.[0];
        if (file?.key) this.saveAvatar(file);
      });
  }

  private saveAvatar(file: UploadedTempFile): void {
    if (!this.employee) return;

    // Show the uploaded file right away, until the employee is saved with the new key
    this.avatarPreviewUrl = file.link;
    this.isUploadingAvatar = true;

    this.crmService
      .updateManager({ avatar_url: file.key }, this.employee.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isUploadingAvatar = false)),
      )
      .subscribe({
        next: (employee: EmployeeProfile) => {
          if (employee?.avatar_url) {
            this.employee = { ...this.employee!, avatar_url: employee.avatar_url };
            this.avatarPreviewUrl = null;
          }
          this.hasChanges = true;
        },
        error: (e) => {
          this.avatarPreviewUrl = null;
          this.showErrors(e);
        },
      });
  }

  private loadEmployee(): void {
    this.crmService
      .getManagerById(this.payload.employeeId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (employee: EmployeeProfile) => (this.employee = employee),
        error: (e) => this.showErrors(e),
      });
  }

  private loadGroups(): void {
    this.crmService
      .getAllGroups({ page: 1, take: 500, access_change_own_role: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => (this.groups = res?.items || []) });
  }

  private showErrors(e: any): void {
    const errors: IError[] = e?.error?.errors || [];
    if (!errors.length) {
      this.notificationService.showError('', ERROR_MESSAGES[DEFAULT_ERROR]);
      return;
    }

    errors.forEach((err) => this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]));
  }
}
